// ABOUTME: Inline requirement search and add component for the applicant offers page.
// ABOUTME: Transforms a button into a searchable combobox; custom requirements stage before saving.

import { Box, Button, FormControl, FormErrorMessage } from '@chakra-ui/react';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { JSX, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Combobox, Icon } from 'shared-components';
import { ApplicantOfferRequirementTask, ApplicantOfferRequirementTaskStatus, IOption } from 'types';
import { ApplicationState } from '../../../../store';
import { createAdditionalRequirements } from '../../../../store/apply/actions';
import { Note } from './Note';

type StagedRequirement = {
  id: string;
  label: string;
  noteText: string;
};

type RequirementOptionGroup = { label: string; options: IOption[] };

enum Mode {
  ButtonView = 'button',
  Searching = 'searching',
  Staging = 'staging',
}

export interface IProps {
  staffOnly?: boolean;
}

export function AddRequirement(props: Readonly<IProps>): JSX.Element {
  const { staffOnly } = props;

  const params = useParams();
  const { userId, offerId, uni } = params;
  const dispatch = useDispatch();
  const additionalRequirements = useSelector((state: ApplicationState) => state.apply.additionalRequirements);

  const [mode, setMode] = useState<Mode>(Mode.ButtonView);
  const [staged, setStaged] = useState<StagedRequirement | null>(null);
  const [showConditionError, setShowConditionError] = useState(false);
  const isSelectingRef = useRef(false);

  const groupedOptions = useMemo((): RequirementOptionGroup[] => {
    if (!additionalRequirements) return [];
    const filtered = staffOnly
      ? additionalRequirements.filter(r => r.type === ApplicantOfferRequirementTask.StaffOnly)
      : additionalRequirements.filter(r => r.type !== ApplicantOfferRequirementTask.StaffOnly);
    const groups: Record<string, IOption[]> = {};
    for (const req of filtered) {
      const groupLabel = req.type;
      if (!groups[groupLabel]) groups[groupLabel] = [];
      groups[groupLabel].push({ value: req.id, label: req.label });
    }
    return Object.entries(groups).map(([label, options]) => ({ label, options }));
  }, [additionalRequirements, staffOnly]);

  const dispatchRequirement = (req: {
    requirementId: string;
    requirementText?: string;
    conditionText?: string;
  }): void => {
    dispatch(
      createAdditionalRequirements({
        applicantId: userId,
        uni,
        offerId,
        requirements: { requirements: [req] },
      }),
    );
  };

  const handleSelect = (newValue: unknown): void => {
    const option = newValue as IOption | null;
    if (!option) return;
    isSelectingRef.current = true;

    dispatchRequirement({ requirementId: option.value });
    setMode(Mode.ButtonView);
  };

  const handleCreate = (inputValue: string): boolean => {
    isSelectingRef.current = true;
    setStaged({ id: staffOnly ? 'StaffOnly' : 'Custom', label: inputValue, noteText: '' });
    setMode(Mode.Staging);
    return true;
  };

  const handleMenuClose = (): void => {
    if (!isSelectingRef.current) {
      setMode(Mode.ButtonView);
    }
    isSelectingRef.current = false;
  };

  const handleConfirm = (): void => {
    if (!staged) return;
    if (!staged.noteText.trim()) {
      setShowConditionError(true);
      return;
    }

    dispatchRequirement({
      requirementId: staged.id,
      requirementText: staged.label,
      ...(staged.noteText.trim() ? { conditionText: staged.noteText.trim() } : {}),
    });

    setShowConditionError(false);
    setStaged(null);
    setMode(Mode.ButtonView);
  };

  const handleCancel = (): void => {
    setShowConditionError(false);
    setStaged(null);
    setMode(Mode.ButtonView);
  };

  if (mode === Mode.ButtonView) {
    return (
      <Button
        variant="outline"
        borderColor="chakra-border-color"
        leftIcon={<Icon icon={faPlus} />}
        borderWidth={1}
        borderRadius={'md'}
        px={6}
        height={12}
        width={'full'}
        justifyContent={'flex-start'}
        onClick={() => setMode(Mode.Searching)}
      >
        {staffOnly ? 'Add staff-only requirements' : 'Add requirements'}
      </Button>
    );
  }

  if (mode === Mode.Searching) {
    return (
      <Box px={2} py={1}>
        <Combobox
          id="add-requirement-search"
          options={groupedOptions}
          isCreatable
          placeholder="Search requirements or enter a custom name"
          onChange={handleSelect}
          onCreateOption={handleCreate}
          selectProps={
            {
              autoFocus: true,
              defaultMenuIsOpen: true,
              // formatCreateLabel is a CreatableSelect prop not in the base ChakraReactSelectProps type
              formatCreateLabel: (inputValue: string): string => `Add '${inputValue}' custom requirement`,
              onMenuClose: handleMenuClose,
            } as unknown as Parameters<typeof Combobox>[0]['selectProps']
          }
        />
      </Box>
    );
  }

  // mode === 'staging' — only reached for custom requirements.
  return (
    <Box borderWidth={1} borderRadius="md" px={6} py={4} display="flex" flexDirection="column" gap={4}>
      <Box wordBreak="break-word">{staged?.label}</Box>
      <FormControl isInvalid={showConditionError}>
        <Note
          status={ApplicantOfferRequirementTaskStatus.Condition}
          noteText={staged?.noteText ?? ''}
          editMode={true}
          onSave={handleConfirm}
          onCancel={handleCancel}
          onEdit={() => {}}
          onNoteChange={(note: string) => {
            setShowConditionError(false);
            setStaged(prev => (prev ? { ...prev, noteText: note } : prev));
          }}
          id="staging-requirement"
          index={0}
        />
        <FormErrorMessage>A condition is required.</FormErrorMessage>
      </FormControl>
    </Box>
  );
}
